/**
 * The route an analytics event's page is on (COM-470, docs/id-free-addresses.md).
 *
 * PostHog knows a page by its address. Over the v1.32 release one page has several: the
 * same record named by its old database id, its slug or its ULID `public_id`. `routeTemplate`
 * gives each of them the route that serves it, with the record's segment named by its param,
 * so that `/parents/young-person/7` and `/parents/young-person/01jbz…` are both
 * `/parents/young-person/:youngPerson`. `usePageTracking` (PostHogAnalytics.jsx) sends it as
 * `compass_route`.
 *
 * Nothing here imports React or React Router, so the unit project can load it.
 */

import {isLegacyNumericKey} from './routeKey';

/**
 * The pages of the role applications whose address names a record, prefix included, in the
 * order React Router ranks them where two could match. The param names are the routes'
 * meaning, not the SPAs' own names for them, so a renamed param leaves the route as it was.
 * A trailing `*` takes whatever follows, as a young person's edit page's own steps do.
 */
const ROUTE_TEMPLATES = [
  '/parents/young-person/:youngPerson',
  '/parents/young-person/:youngPerson/edit/*',
  '/parents/:category/action-list',
  '/professionals/parent-categories/:category',
  '/professionals/parent-categories/:category/item/:action',
  '/professionals/young-person-categories/:category',
  '/professionals/young-person-categories/:category/item/:topic',
  '/professionals/key-duties/stage/:stage',
  '/professionals/key-duties/stage/:stage/duty/:duty',
  '/young-people/topics/:topic',
] as const;

/**
 * The params whose page names its record by a new key, and puts it in place of an old
 * numeric id the address arrives with (`useCanonicalRouteParams`), by the template that
 * holds them. Each ticket that moves a page adds its own (COM-474: a Parent's young person;
 * COM-475: a Parent's category; COM-477: the Professional resource pages' categories,
 * actions and topics; COM-478: the Professional key duties' stages and duties; COM-480: a
 * young person's topic).
 */
const REPLACED_LEGACY_PARAMS: ReadonlyMap<string, readonly string[]> = new Map([
  ['/parents/young-person/:youngPerson', ['youngPerson']],
  ['/parents/young-person/:youngPerson/edit/*', ['youngPerson']],
  ['/parents/:category/action-list', ['category']],
  ['/professionals/parent-categories/:category', ['category']],
  ['/professionals/parent-categories/:category/item/:action', ['category', 'action']],
  ['/professionals/young-person-categories/:category', ['category']],
  ['/professionals/young-person-categories/:category/item/:topic', ['category', 'topic']],
  ['/professionals/key-duties/stage/:stage', ['stage']],
  ['/professionals/key-duties/stage/:stage/duty/:duty', ['stage', 'duty']],
  ['/young-people/topics/:topic', ['topic']],
]);

/**
 * A pathname's segments as React Router matches them (`decodePath` in react-router 8.3.0):
 * each one percent-decoded, an encoded slash kept encoded, or all of them as written when
 * any holds a malformed escape, as React Router then leaves the whole path. So
 * `/parents/young%2Dperson/%37` is the page React Router shows for `/parents/young-person/7`.
 */
function routerSegments(pathname: string): string[] {
  const segments = pathname.split('/');
  try {
    return segments.map((segment) => decodeURIComponent(segment).replace(/\//g, '%2F'));
  } catch {
    return segments;
  }
}

/**
 * Whether `segments` (a pathname's, as `routerSegments` gives them) are a page of `template`:
 * a param takes any segment but an empty one, and a word matches in any case, as React Router matches it.
 * After the template's own segments there may be a trailing slash, or anything at all after a
 * `*`.
 */
function matches(template: string, segments: readonly string[]): boolean {
  const pattern = template.split('/');
  const splat = pattern[pattern.length - 1] === '*';
  const fixed = splat ? pattern.slice(0, -1) : pattern;
  if (segments.length < fixed.length || (!splat && segments.slice(fixed.length).join('/') !== '')) return false;

  return fixed.every((part, at) => (part.startsWith(':') ? segments[at] !== '' : part === segments[at].toLowerCase()));
}

/**
 * The route of `pathname` (the whole path, the application's prefix included): the first
 * template that matches it, as the template is written, so that one value names every page
 * of it. Any other path is its own route.
 */
export function routeTemplate(pathname: string): string {
  const segments = routerSegments(pathname);
  return ROUTE_TEMPLATES.find((template) => matches(template, segments)) ?? pathname;
}

/**
 * Whether `pathname` names a record by the old numeric id of a param that has moved: an
 * address its page never stays at, but replaces in place by the record's new key, or shows
 * as not found when the record is not one it holds. `usePageTracking` sends no pageview for
 * one (COM-474), so no pageview's address holds the old id.
 */
export function isReplacedLegacyAddress(pathname: string): boolean {
  const segments = routerSegments(pathname);
  const template = ROUTE_TEMPLATES.find((candidate) => matches(candidate, segments));
  const params = template === undefined ? undefined : REPLACED_LEGACY_PARAMS.get(template);
  if (template === undefined || params === undefined) return false;

  const pattern = template.split('/');
  return params.some((param) => isLegacyNumericKey(segments[pattern.indexOf(`:${param}`)]));
}
